"use client";

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNotificationContext } from './NotificationProvider';

interface NotificationSystemProps {
  className?: string;
}

// Lê das mesmas notificações reais do sino (NotificationProvider →
// in_app_notifications) — antes esta página mostrava dados mockados fixos,
// diferentes das notificações reais do sino no cabeçalho.
export const NotificationSystem: React.FC<NotificationSystemProps> = ({ className }) => {
  const { notifications, stats, markAsRead, markAllAsRead, removeNotification } = useNotificationContext();
  const [activeTab, setActiveTab] = useState('all');
  const unreadCount = stats.unread;
  const navigate = useNavigate();

  const filteredNotifications = notifications.filter(notification => {
    if (activeTab === 'all') return true;
    if (activeTab === 'unread') return !notification.read;
    return notification.category === activeTab;
  });

  const deleteNotification = (id: string) => {
    removeNotification(id);
  };

  const getTypeIcon = (type: string) => {
    const icons = {
      info: 'ℹ️',
      success: '✅',
      warning: '⚠️',
      error: '❌'
    };
    return icons[type as keyof typeof icons] || 'ℹ️';
  };

  const getCategoryIcon = (category: string) => {
    const icons = {
      appointment: '📅',
      payment: '💳',
      service: '🔧',
      general: '📢',
      promotion: '🎉'
    };
    return icons[category as keyof typeof icons] || '📢';
  };

  const getTypeColor = (type: string) => {
    const colors = {
      info: 'bg-blue-100 text-blue-800',
      success: 'bg-green-100 text-green-800',
      warning: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-100 text-red-800'
    };
    return colors[type as keyof typeof colors] || colors.info;
  };

  return (
    <div className={className}>
      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Notificações</h2>
          <p className="text-muted-foreground">
            Acompanhe suas notificações e atualizações
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {unreadCount > 0 && (
            <Button variant="outline" onClick={markAllAsRead}>
              <span className="text-lg mr-2">✅</span>
              Marcar todas como lidas
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/settings')}>
            <span className="text-lg mr-2">⚙️</span>
            Configurações
          </Button>
        </div>
      </div>

      {/* Estatísticas Rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <span className="text-lg">📢</span>
              <div>
                <p className="text-sm font-medium">Total</p>
                <p className="text-2xl font-bold">{notifications.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <span className="text-lg">🔔</span>
              <div>
                <p className="text-sm font-medium">Não Lidas</p>
                <p className="text-2xl font-bold text-red-600">{unreadCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <span className="text-lg">📅</span>
              <div>
                <p className="text-sm font-medium">Agendamentos</p>
                <p className="text-2xl font-bold">
                  {notifications.filter(n => n.category === 'appointment').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <span className="text-lg">💳</span>
              <div>
                <p className="text-sm font-medium">Pagamentos</p>
                <p className="text-2xl font-bold">
                  {notifications.filter(n => n.category === 'payment').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs de Conteúdo */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex w-full overflow-x-auto gap-1 p-1 sm:grid sm:grid-cols-3 lg:grid-cols-6">
          <TabsTrigger value="all">Todas</TabsTrigger>
          <TabsTrigger value="unread">Não Lidas</TabsTrigger>
          <TabsTrigger value="appointment">Agendamentos</TabsTrigger>
          <TabsTrigger value="payment">Pagamentos</TabsTrigger>
          <TabsTrigger value="service">Serviços</TabsTrigger>
          <TabsTrigger value="promotion">Promoções</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {filteredNotifications.length > 0 ? (
            <div className="space-y-4">
              {filteredNotifications.map((notification) => (
                <Card 
                  key={notification.id} 
                  className={`hover:shadow-md transition-shadow ${
                    !notification.read ? 'border-l-4 border-l-blue-500 bg-blue-50/50' : ''
                  }`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-lg">
                            {getTypeIcon(notification.type)}
                          </span>
                          <span className="text-lg">
                            {getCategoryIcon(notification.category)}
                          </span>
                          <h3 className="font-semibold">{notification.title}</h3>
                          <Badge className={getTypeColor(notification.type)}>
                            {notification.type}
                          </Badge>
                          {!notification.read && (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                              Nova
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>
                            {new Date(notification.created_at).toLocaleString('pt-BR')}
                          </span>
                          <span className="capitalize">
                            {notification.category}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        {!notification.read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => markAsRead(notification.id)}
                            title="Marcar como lida"
                          >
                            <span className="text-lg">✅</span>
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteNotification(notification.id)}
                          title="Remover"
                        >
                          <span className="text-lg">🗑️</span>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📢</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhuma notificação encontrada
              </h3>
              <p className="text-gray-600">
                {activeTab === 'unread' 
                  ? 'Você não tem notificações não lidas'
                  : 'Você não tem notificações nesta categoria'
                }
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
